import * as THREE from "three";
import { prefersReducedMotion, isWebGLAvailable } from "./config.js";

/**
 * Ambient background for sub-pages: a few hundred drifting bioluminescent
 * motes. No post-processing, tiny footprint — just enough to keep the
 * world of the homepage alive across the site.
 */
const VERT = /* glsl */ `
  uniform float uTime;
  uniform float uPixelRatio;

  attribute float aSeed;
  attribute float aSize;
  attribute vec3 aColor;

  varying vec3 vColor;
  varying float vTwinkle;

  void main() {
    vec3 p = position;
    p.x += sin(uTime * 0.05 + aSeed * 6.2831) * 1.4;
    p.y += mod(uTime * (0.12 + aSeed * 0.22) + aSeed * 40.0, 44.0) - 22.0;
    p.z += cos(uTime * 0.04 + aSeed * 6.2831) * 1.2;

    vec4 mv = modelViewMatrix * vec4(p, 1.0);
    float twinkle = 0.55 + 0.45 * sin(uTime * (0.6 + aSeed) + aSeed * 31.0);
    gl_PointSize = aSize * twinkle * uPixelRatio * (90.0 / -mv.z);
    gl_PointSize = min(gl_PointSize, 10.0 * uPixelRatio);

    vColor = aColor;
    vTwinkle = twinkle;
    gl_Position = projectionMatrix * mv;
  }
`;

const FRAG = /* glsl */ `
  precision highp float;
  varying vec3 vColor;
  varying float vTwinkle;

  void main() {
    vec2 uv = gl_PointCoord - 0.5;
    float d = length(uv);
    float disc = smoothstep(0.5, 0.05, d);
    gl_FragColor = vec4(vColor, disc * disc * vTwinkle * 0.55);
  }
`;

const COLORS = [
  [0.62, 1.0, 0.34], // lime
  [0.36, 0.91, 1.0], // cyan
  [0.54, 0.44, 1.0], // violet
  [1.0, 0.36, 0.82], // magenta
  [1.0, 0.71, 0.34] // amber
];

function main() {
  if (prefersReducedMotion() || !isWebGLAvailable()) return;
  // The homepage runs the full experience instead.
  if (document.getElementById("xpScrollSpace")) return;

  const canvas = document.createElement("canvas");
  canvas.className = "bg-canvas";
  canvas.setAttribute("aria-hidden", "true");
  document.body.appendChild(canvas);

  const renderer = new THREE.WebGLRenderer({
    canvas,
    alpha: true,
    antialias: false,
    powerPreference: "low-power"
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.5));

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(
    55,
    window.innerWidth / Math.max(1, window.innerHeight),
    0.1,
    80
  );
  camera.position.set(0, 0, 16);

  const COUNT = Math.min(window.innerWidth, window.innerHeight) < 760 ? 240 : 520;
  const positions = new Float32Array(COUNT * 3);
  const seeds = new Float32Array(COUNT);
  const sizes = new Float32Array(COUNT);
  const colors = new Float32Array(COUNT * 3);

  for (let i = 0; i < COUNT; i++) {
    positions[i * 3] = (Math.random() - 0.5) * 46;
    positions[i * 3 + 1] = (Math.random() - 0.5) * 44;
    positions[i * 3 + 2] = -4 - Math.random() * 26;
    seeds[i] = Math.random();
    sizes[i] = 0.7 + Math.random() * 1.4;
    const c = COLORS[Math.floor(Math.random() * COLORS.length)];
    const dim = 0.5 + Math.random() * 0.5;
    colors[i * 3] = c[0] * dim;
    colors[i * 3 + 1] = c[1] * dim;
    colors[i * 3 + 2] = c[2] * dim;
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geo.setAttribute("aSeed", new THREE.BufferAttribute(seeds, 1));
  geo.setAttribute("aSize", new THREE.BufferAttribute(sizes, 1));
  geo.setAttribute("aColor", new THREE.BufferAttribute(colors, 3));

  const mat = new THREE.ShaderMaterial({
    vertexShader: VERT,
    fragmentShader: FRAG,
    uniforms: {
      uTime: { value: 0 },
      uPixelRatio: { value: Math.min(window.devicePixelRatio || 1, 1.5) }
    },
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending
  });

  const points = new THREE.Points(geo, mat);
  points.frustumCulled = false;
  scene.add(points);

  function resize() {
    const w = window.innerWidth;
    const h = window.innerHeight;
    camera.aspect = w / Math.max(1, h);
    camera.updateProjectionMatrix();
    renderer.setSize(w, h, false);
  }
  window.addEventListener("resize", resize, { passive: true });
  resize();

  let running = true;
  document.addEventListener(
    "visibilitychange",
    () => {
      running = document.visibilityState === "visible";
    },
    { passive: true }
  );

  const clock = new THREE.Clock();
  let t = 0;

  renderer.setAnimationLoop(() => {
    if (!running) return;
    t += Math.min(clock.getDelta(), 0.05);
    mat.uniforms.uTime.value = t;
    // Subtle scroll parallax.
    camera.position.y = -window.scrollY * 0.0016;
    renderer.render(scene, camera);
  });

  canvas.classList.add("is-ready");
}

try {
  main();
} catch (e) {
  window.__debugLastError = e?.message || String(e);
}
